import { ApiResponse, OmitFirstArg } from '../types';
import ky, { KyInstance, Options } from 'ky';

// Claim-leg reads for the ADR-048-style fee-share referral program (ported
// from AIP). getReferralEarnings/ReferralEarnings live in user.ts (already
// shipped 7/16) -- this file only adds the claim-proof/payouts/contributions
// endpoints that sit on the same `/referral` prefix.

// One currency's Merkle claim proof. Empty fields are omitted by the server
// while rootPublished=false, so the FE must branch on that flag alone --
// NEVER infer claimability from cumulativeAmount/proof presence without it.
export type ReferralClaimProof = {
  rootPublished: boolean;
  account?: string;
  currency?: string;
  cumulativeAmount?: string;
  root?: string;
  proof?: string[];
  epoch?: number;
};

export type ReferralClaimProofParams = {
  // 0x token address (lowercase collateral address), NOT a symbol -- matches
  // the `currency` field returned by getReferralEarnings, which must be
  // passed back here unchanged.
  currency: string;
};

// One completed on-chain payout (claim history). `amount` is a positive raw
// decimal string at the ledger's fixed 18dp scale. `createdAt` is unix
// seconds, newest first, capped at 100 rows server-side.
export type ReferralPayout = {
  currency: string;
  amount: string;
  txHash: string;
  createdAt: number;
};

export type ReferralPayouts = {
  payouts: ReferralPayout[];
};

// One referee's cumulative fee-share contribution in one currency. `total` is
// a positive raw decimal string, same fixed 18dp scale as ReferralPayout.amount.
// name/avatar may be blank strings (referee profile unresolved).
export type ReferralContribution = {
  refereeUid: string;
  refereeName: string;
  refereeAvatar: string;
  currency: string;
  total: string;
};

export type ReferralContributions = {
  contributions: ReferralContribution[];
};

/**
 * Get the caller's Merkle claim proof for one currency.
 * [need auth] -- uid is resolved from the JWT server-side; `currency` selects
 * which token's proof to fetch. NOTE: unlike the other routes here, this
 * endpoint's handler writes the payload directly (no `{code,data}` envelope) --
 * do NOT wrap the return type in ApiResponse<T> or every field reads undefined.
 * @param api The KyInstance (referral-prefixed)
 * @param searchParams { currency: <0x token address> }
 * @param options Additional request options
 * @example
 * const proof = await getReferralClaimProof({ currency: baseTokenAddress });
 */
export const getReferralClaimProof = async (
  api: KyInstance,
  searchParams: ReferralClaimProofParams,
  options?: Options
): Promise<ReferralClaimProof> => {
  return await api.get<ReferralClaimProof>('claim-proof', { ...options, searchParams }).json();
};

/**
 * Get the caller's completed payout history, newest first (max 100).
 * [need auth] -- uid is resolved from the JWT server-side, never a param.
 * Standard {code,data} envelope -- unwrap `.data`.
 * @param api The KyInstance (referral-prefixed)
 * @param options Additional request options
 * @example
 * const { data } = await getReferralPayouts();
 */
export const getReferralPayouts = async (api: KyInstance, options?: Options): Promise<ApiResponse<ReferralPayouts>> => {
  return await api.get<ApiResponse<ReferralPayouts>>('payouts', options).json();
};

/**
 * Get the caller's per-referee cumulative contributions, ordered desc.
 * [need auth] -- uid is resolved from the JWT server-side, never a param.
 * Standard {code,data} envelope -- unwrap `.data`.
 * @param api The KyInstance (referral-prefixed)
 * @param options Additional request options
 * @example
 * const { data } = await getReferralContributions();
 */
export const getReferralContributions = async (
  api: KyInstance,
  options?: Options
): Promise<ApiResponse<ReferralContributions>> => {
  return await api.get<ApiResponse<ReferralContributions>>('contributions', options).json();
};

export type ReferralApi = {
  getReferralClaimProof: OmitFirstArg<typeof getReferralClaimProof>;
  getReferralPayouts: OmitFirstArg<typeof getReferralPayouts>;
  getReferralContributions: OmitFirstArg<typeof getReferralContributions>;
};

export const createReferralApi = (initOptions: Options): ReferralApi => {
  const mergedOptions = { ...initOptions, prefixUrl: `${initOptions.prefixUrl}/referral` } as Options;
  const referralApi = ky.create(mergedOptions);
  return {
    getReferralClaimProof: (searchParams, options) => getReferralClaimProof(referralApi, searchParams, options),
    getReferralPayouts: options => getReferralPayouts(referralApi, options),
    getReferralContributions: options => getReferralContributions(referralApi, options),
  };
};

export default createReferralApi;
